import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function PricingPage() {
  const [cycle, setCycle] = useState('monthly'); // monthly | annual
  const discount = cycle === 'annual' ? 0.8 : 1;

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '96px 0 64px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', left: '-10%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(107,47,217,0.4) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative' }}>
          <span className="chip" style={{ marginBottom: 20 }}>💎 Pricing</span>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 18 }}>
            Simple pricing. <span className="gradient-text">Real value.</span>
          </h1>
          <p style={{ fontSize: 18, opacity: 0.86, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            For shoppers, PerkVora is free forever. For businesses and publishers, choose the plan that matches your ambition.
          </p>
        </div>
      </section>

      {/* Shopper free banner */}
      <section style={{ padding: '40px 0', background: '#fff' }}>
        <div className="container">
          <div style={{ background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 20, padding: '32px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#6B2FD9', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>For shoppers</p>
              <h3 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>Always free. No tricks, no credit card.</h3>
              <p style={{ fontSize: 14, color: '#4A5568', marginTop: 6 }}>Access every deal, every cashback, every perk — the way it should be.</p>
            </div>
            <Link to="/register" className="btn btn-primary btn-lg">Join free →</Link>
          </div>
        </div>
      </section>

      {/* Toggle */}
      <section style={{ padding: '32px 0', background: '#fff' }}>
        <div className="container text-center" style={{ marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', background: '#F7F8FC', padding: 4, borderRadius: 999, gap: 4 }}>
            <ToggleBtn active={cycle === 'monthly'} onClick={() => setCycle('monthly')}>Monthly</ToggleBtn>
            <ToggleBtn active={cycle === 'annual'} onClick={() => setCycle('annual')}>
              Annual <span style={{ fontSize: 10, background: 'var(--brand-gradient)', color: '#fff', padding: '2px 8px', borderRadius: 999, marginLeft: 6, fontWeight: 700 }}>Save 20%</span>
            </ToggleBtn>
          </div>
        </div>

        {/* Plans */}
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, alignItems: 'stretch' }} className="plans-grid">
            <Plan
              name="Starter" tagline="For small brands getting started"
              price="Free" priceNote="forever"
              cta="Get started" to="/register?role=merchant"
              features={['List up to 3 deals', 'Self-serve merchant portal', 'Basic analytics (impressions, clicks)', 'Community support', 'Pay 8% commission on redemptions']} />
            <Plan
              name="Growth" tagline="For growing merchants"
              price={`$${Math.round(49 * discount)}`} priceNote={cycle === 'annual' ? '/mo billed annually' : '/month'}
              cta="Start free trial" to="/register?role=merchant&plan=growth"
              features={['Unlimited deals', 'Advanced analytics + ROAS', 'Featured placement credits', 'Email & chat support', 'Pay 6% commission on redemptions', 'A/B test deals']} />
            <Plan
              featured name="Pro" tagline="For ambitious brands at scale"
              price={`$${Math.round(199 * discount)}`} priceNote={cycle === 'annual' ? '/mo billed annually' : '/month'}
              cta="Start free trial" to="/register?role=merchant&plan=pro"
              features={['Everything in Growth', 'Homepage feature slots', 'API access + webhooks', 'Priority support (4h SLA)', 'Pay 4% commission on redemptions', 'Dedicated account manager', 'Custom reporting']} />
            <Plan
              name="Enterprise" tagline="For publishers & networks"
              price="Custom" priceNote="talk to sales"
              cta="Contact sales" to="/contact?topic=enterprise"
              features={['White-label platform', 'Custom domain & branding', 'Multi-tenant management', 'SSO (SAML / OIDC)', 'Revenue share model', '24/7 dedicated support', 'SLA & compliance options']} />
          </div>
          <style>{`
            @media(max-width:1100px){.plans-grid{grid-template-columns:1fr 1fr!important}}
            @media(max-width:640px){.plans-grid{grid-template-columns:1fr!important}}
          `}</style>
        </div>
      </section>

      {/* Compare */}
      <section className="section section-tinted">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 40 }}>
            <span className="eyebrow">Feature comparison</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>What's included at every tier</h2>
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid #E4E7F0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#F7F8FC' }}>
                  <th style={th}>Feature</th>
                  <th style={th}>Starter</th>
                  <th style={th}>Growth</th>
                  <th style={{ ...th, background: '#F3EEFF', color: '#6B2FD9' }}>Pro</th>
                  <th style={th}>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Deals per month" c1="3" c2="Unlimited" c3="Unlimited" c4="Unlimited" />
                <Row label="Analytics dashboard" c1="Basic" c2="Advanced" c3="Advanced + ROAS" c4="Custom" />
                <Row label="Featured placements" c1="—" c2="5 / mo" c3="Unlimited" c4="Unlimited" />
                <Row label="API + webhooks" c1="—" c2="—" c3="✓" c4="✓" />
                <Row label="A/B testing" c1="—" c2="✓" c3="✓" c4="✓" />
                <Row label="Support SLA" c1="Community" c2="24h" c3="4h" c4="24/7" />
                <Row label="Commission rate" c1="8%" c2="6%" c3="4%" c4="Negotiated" />
                <Row label="White-label" c1="—" c2="—" c3="—" c4="✓" />
                <Row label="SSO" c1="—" c2="—" c3="—" c4="✓" />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ mini */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container-narrow">
          <h2 className="section-h2">Pricing FAQ</h2>
          <p className="section-lead">Can't find the answer you're looking for? <Link to="/contact">Get in touch.</Link></p>
          <div>
            <MiniFaq q="Is PerkVora really free for shoppers?" a="Yes, forever. We make money from our merchant partners, never from you. No paywalls, no premium-only deals, no selling your data." />
            <MiniFaq q="Can I switch plans anytime?" a="Yes. Upgrade or downgrade at any time. Upgrades take effect immediately; downgrades at the end of your billing cycle. No contracts or cancellation fees." />
            <MiniFaq q="What payment methods do you accept?" a="All major credit cards, ACH, and Stripe-supported payment methods. Enterprise customers can pay by invoice with Net 30 terms." />
            <MiniFaq q="How does the commission model work?" a="You're only charged commission when a PerkVora user successfully redeems your deal and completes a purchase. No redemption, no charge. Commission is deducted from your merchant balance." />
            <MiniFaq q="Do you offer nonprofit discounts?" a="Yes. Verified 501(c)(3) nonprofits get 50% off Growth and Pro plans. Contact sales with your EIN." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="cta-banner">
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 14, lineHeight: 1.1 }}>
              Not sure which plan?
            </h2>
            <p style={{ fontSize: 16, maxWidth: 520, margin: '0 auto 32px', opacity: 0.95 }}>Book a 15-min call with our team. We'll help you pick the plan that matches your goals.</p>
            <Link to="/contact" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>
              Talk to sales →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const th = { textAlign: 'left', padding: '16px 20px', fontSize: 13, fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #E4E7F0' };

function ToggleBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 22px', borderRadius: 999, border: 'none', cursor: 'pointer',
      background: active ? '#fff' : 'transparent', color: active ? '#0F1225' : '#4A5568',
      fontWeight: 600, fontSize: 14, fontFamily: 'inherit',
      boxShadow: active ? '0 2px 8px rgba(15,18,37,0.08)' : 'none',
      transition: 'all 0.15s',
    }}>{children}</button>
  );
}

function Plan({ name, tagline, price, priceNote, features, cta, to, featured }) {
  return (
    <div className={featured ? 'plan-card plan-featured' : 'plan-card'}>
      {featured && <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: 'var(--brand-gradient)', color: '#fff', padding: '5px 16px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Most popular</div>}
      <p className="plan-name">{name}</p>
      <p className="plan-desc">{tagline}</p>
      <div style={{ marginBottom: 24 }}>
        <span className="plan-price">{price}</span>
        <div style={{ fontSize: 13, color: '#8B94A8', marginTop: 4 }}>{priceNote}</div>
      </div>
      <Link to={to} className={featured ? 'btn btn-primary btn-full' : 'btn btn-outline btn-full'} style={{ marginBottom: 24 }}>{cta}</Link>
      <div style={{ borderTop: '1px solid #E4E7F0', paddingTop: 18 }}>
        {features.map(f => <div key={f} className="plan-feature">{f}</div>)}
      </div>
    </div>
  );
}

function Row({ label, c1, c2, c3, c4 }) {
  const cell = { padding: '14px 20px', fontSize: 14, color: '#4A5568', borderBottom: '1px solid #F0F2F8' };
  const featCell = { ...cell, background: '#FAF7FF', fontWeight: 600, color: '#0F1225' };
  return (
    <tr>
      <td style={{ ...cell, fontWeight: 600, color: '#0F1225' }}>{label}</td>
      <td style={cell}>{c1}</td>
      <td style={cell}>{c2}</td>
      <td style={featCell}>{c3}</td>
      <td style={cell}>{c4}</td>
    </tr>
  );
}

function MiniFaq({ q, a }) {
  return (
    <div style={{ padding: '24px 0', borderBottom: '1px solid #E4E7F0' }}>
      <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.01em' }}>{q}</h4>
      <p style={{ fontSize: 14, color: '#4A5568', lineHeight: 1.7 }}>{a}</p>
    </div>
  );
}
